// /dev/coloring — internal playground for the coloring engine. Not linked
// from anywhere user-facing. Features dogfood-only knobs that don't belong
// in the production ColoringPage:
//
//   ?lineart=/assets/...     — override which lineart loads (default coat)
//   bucket / brush toggle    — same as production
//   brush radius slider      — drag to find the sweet spot per lineart
//
// No R2, no autosave, no history sync — pure local state for testing the
// engine + new asset prompts.

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PointerEvent,
} from 'react'
import { useSearchParams } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  ColoringEngine,
  JOSEPH_PALETTE,
  NATIVITY_PALETTE,
  type FillCandidate,
  type RGB,
  type Swatch,
} from '@skena/coloring'

const CANVAS_W = 1408
const CANVAS_H = 786
const FILL_DURATION_MS = 150
const UNDO_FADE_MS = 200
const DEFAULT_LINEART = '/assets/packs/joseph/items/coat-lineart.png'
const DEFAULT_BRUSH_RADIUS = 40

type Tool = 'bucket' | 'brush'
type PaletteId = 'joseph' | 'nativity'
const PALETTES: Record<PaletteId, readonly Swatch[]> = {
  joseph: JOSEPH_PALETTE,
  nativity: NATIVITY_PALETTE,
}

interface OnLineMarker {
  id: number
  x: number
  y: number
}

export function ColoringPlayground() {
  const [searchParams] = useSearchParams()
  const lineartUrl = searchParams.get('lineart') ?? DEFAULT_LINEART

  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const engineRef = useRef<ColoringEngine | null>(null)
  const tempCanvasRef = useRef<HTMLCanvasElement | null>(null)
  const animatingRef = useRef(false)
  const drawingRef = useRef(false)
  const lastBrushPosRef = useRef<{ x: number; y: number } | null>(null)

  const [paletteId, setPaletteId] = useState<PaletteId>('joseph')
  const [colorIdx, setColorIdx] = useState(0)
  const [fillCount, setFillCount] = useState(0)
  const [onLineMarker, setOnLineMarker] = useState<OnLineMarker | null>(null)
  const [toast, setToast] = useState<string | null>(null)
  const [tool, setTool] = useState<Tool>('bucket')
  const [brushRadius, setBrushRadius] = useState(DEFAULT_BRUSH_RADIUS)

  const palette = PALETTES[paletteId]
  const activeColor: RGB = palette[colorIdx]?.rgb ?? palette[0]!.rgb

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    let cancelled = false

    canvas.width = CANVAS_W
    canvas.height = CANVAS_H
    const engine = new ColoringEngine(canvas, { devChecks: import.meta.env.DEV })
    engineRef.current = engine

    const temp = document.createElement('canvas')
    temp.width = CANVAS_W
    temp.height = CANVAS_H
    tempCanvasRef.current = temp

    const ctx = canvas.getContext('2d')!
    const img = new window.Image()
    img.onload = () => {
      // Race guard: when ?lineart switches mid-load, the previous img can
      // resolve after the new effect run started. Without this check the
      // late-loader stomps the new lineart back to the old one.
      if (cancelled) return
      ctx.clearRect(0, 0, CANVAS_W, CANVAS_H)
      ctx.fillStyle = '#ffffff'
      ctx.fillRect(0, 0, CANVAS_W, CANVAS_H)
      ctx.drawImage(img, 0, 0, CANVAS_W, CANVAS_H)
      engine.loadImageData(ctx.getImageData(0, 0, CANVAS_W, CANVAS_H))
      setFillCount(0)
    }
    img.onerror = () => {
      if (cancelled) return
      drawPlaceholderLineart(canvas)
      binarizeCanvas(canvas)
      engine.loadImageData(ctx.getImageData(0, 0, CANVAS_W, CANVAS_H))
    }
    // Cache-bust so a stale dev SW or browser cache can't pin an old PNG
    // (or, worse, a 404 that loops onto the placeholder fallback). This is
    // dev-only — the production ColoringPage is fine, its lineart fetch
    // path doesn't go through this playground.
    img.src = lineartUrl + (lineartUrl.includes('?') ? '&' : '?') + '_=' + Date.now()

    return () => {
      cancelled = true
    }
  }, [lineartUrl])

  const showToast = useCallback((msg: string) => {
    setToast(msg)
    const t = setTimeout(() => setToast(null), 1400)
    return () => clearTimeout(t)
  }, [])

  const canvasCoords = (
    e: PointerEvent<HTMLCanvasElement>,
    canvas: HTMLCanvasElement,
  ): { x: number; y: number; cssX: number; cssY: number } => {
    const rect = canvas.getBoundingClientRect()
    const cssX = e.clientX - rect.left
    const cssY = e.clientY - rect.top
    return {
      x: (cssX * canvas.width) / rect.width,
      y: (cssY * canvas.height) / rect.height,
      cssX,
      cssY,
    }
  }

  const onPointerDown = useCallback(
    (e: PointerEvent<HTMLCanvasElement>) => {
      const engine = engineRef.current
      const canvas = canvasRef.current
      if (!engine || !canvas || animatingRef.current) return
      const { x, y, cssX, cssY } = canvasCoords(e, canvas)

      if (tool === 'bucket') {
        const { result, candidate } = engine.prepareFill(x, y, activeColor)
        if (!candidate) {
          if (result.status === 'on-line') {
            setOnLineMarker({ id: Date.now(), x: cssX, y: cssY })
            showToast('點顏色區塊內喔')
            setTimeout(() => setOnLineMarker(null), 600)
          } else if (result.status === 'too-small') {
            showToast(`太小（${result.filledPixelCount} px）`)
          } else if (result.status === 'too-large') {
            showToast('破口太大——切到🖌️塗')
          }
          return
        }
        animatingRef.current = true
        animateRippleReveal(canvas, tempCanvasRef.current!, candidate, FILL_DURATION_MS)
          .then(() => {
            candidate.commit()
            setFillCount(engine.fillCount())
          })
          .catch((err: unknown) => {
            console.error('[playground] ripple failed:', err)
            candidate.cancel()
          })
          .finally(() => {
            animatingRef.current = false
          })
        return
      }

      drawingRef.current = true
      lastBrushPosRef.current = { x, y }
      canvas.setPointerCapture(e.pointerId)
      engine.brushStamp(x, y, brushRadius, activeColor)
    },
    [activeColor, showToast, tool, brushRadius],
  )

  const onPointerMove = useCallback(
    (e: PointerEvent<HTMLCanvasElement>) => {
      if (tool !== 'brush' || !drawingRef.current) return
      const engine = engineRef.current
      const canvas = canvasRef.current
      if (!engine || !canvas) return
      const { x, y } = canvasCoords(e, canvas)
      const last = lastBrushPosRef.current
      if (!last) {
        engine.brushStamp(x, y, brushRadius, activeColor)
        lastBrushPosRef.current = { x, y }
        return
      }
      const dx = x - last.x
      const dy = y - last.y
      const dist = Math.hypot(dx, dy)
      if (dist < 1) return
      const spacing = Math.max(1, brushRadius / 2)
      const steps = Math.max(1, Math.ceil(dist / spacing))
      for (let i = 1; i <= steps; i++) {
        const t = i / steps
        engine.brushStamp(last.x + dx * t, last.y + dy * t, brushRadius, activeColor)
      }
      lastBrushPosRef.current = { x, y }
    },
    [activeColor, tool, brushRadius],
  )

  const onPointerUp = useCallback(
    (e: PointerEvent<HTMLCanvasElement>) => {
      if (tool !== 'brush' || !drawingRef.current) return
      const engine = engineRef.current
      const canvas = canvasRef.current
      if (!engine || !canvas) return
      drawingRef.current = false
      lastBrushPosRef.current = null
      try {
        canvas.releasePointerCapture(e.pointerId)
      } catch {
        /* ignore */
      }
      engine.commitBrushStroke()
      setFillCount(engine.fillCount())
    },
    [tool],
  )

  const onUndo = useCallback(() => {
    const engine = engineRef.current
    const canvas = canvasRef.current
    if (!engine || !canvas) return
    if (!engine.canUndo()) return
    const ctx = canvas.getContext('2d')!
    const before = ctx.getImageData(0, 0, canvas.width, canvas.height)
    engine.undo()
    const after = ctx.getImageData(0, 0, canvas.width, canvas.height)
    fadeBetween(canvas, tempCanvasRef.current!, before, after, UNDO_FADE_MS).then(() => {
      setFillCount(engine.fillCount())
    })
  }, [])

  const onReset = useCallback(() => {
    const engine = engineRef.current
    if (!engine) return
    engine.resetToInitial()
    setFillCount(engine.fillCount())
  }, [])

  return (
    <main className="mx-auto flex min-h-full max-w-3xl flex-col gap-4 p-6">
      <header className="flex items-baseline justify-between">
        <div>
          <h1 className="font-[family-name:var(--font-display)] text-2xl text-[var(--color-fg)]">
            Coloring Playground
          </h1>
          <p className="text-xs text-[var(--color-muted)]">
            /dev only · 填了 {fillCount} 次 · {lineartUrl}
          </p>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <button
            onClick={() => setPaletteId('joseph')}
            className={paletteId === 'joseph' ? 'underline underline-offset-4' : 'opacity-60'}
          >
            約瑟
          </button>
          <span className="text-[var(--color-muted)]">·</span>
          <button
            onClick={() => setPaletteId('nativity')}
            className={paletteId === 'nativity' ? 'underline underline-offset-4' : 'opacity-60'}
          >
            降生
          </button>
        </div>
      </header>

      <div className="relative mx-auto w-full max-w-3xl">
        <canvas
          ref={canvasRef}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          className="block w-full touch-none rounded-[var(--radius-soft)] border border-[var(--color-muted)]/20 bg-white shadow-sm select-none"
          style={{ aspectRatio: '1408 / 786', cursor: 'crosshair' }}
          aria-label="coloring canvas"
        />
        <AnimatePresence>
          {onLineMarker && (
            <motion.div
              key={onLineMarker.id}
              initial={{ opacity: 0, scale: 0.8, x: '-50%', y: '-50%' }}
              animate={{
                opacity: [0, 1, 1, 0],
                scale: [0.8, 1.15, 1, 0.95],
                x: ['-52%', '-48%', '-52%', '-50%'],
                y: '-50%',
              }}
              transition={{ duration: 0.55, times: [0, 0.2, 0.7, 1] }}
              exit={{ opacity: 0 }}
              className="pointer-events-none absolute size-7 rounded-full border-2 border-[var(--color-joseph-primary)]"
              style={{ left: onLineMarker.x, top: onLineMarker.y }}
              aria-hidden="true"
            />
          )}
        </AnimatePresence>
      </div>

      <div className="flex flex-wrap items-center justify-center gap-3 text-sm">
        <div className="flex items-center gap-2" role="radiogroup" aria-label="工具">
          <button
            onClick={() => setTool('bucket')}
            aria-checked={tool === 'bucket'}
            role="radio"
            className={`rounded-[var(--radius-pill)] bg-white px-3 py-1.5 shadow-sm ${
              tool === 'bucket' ? 'ring-2 ring-[var(--color-joseph-primary)]' : 'opacity-60'
            }`}
          >
            🪣 填色
          </button>
          <button
            onClick={() => setTool('brush')}
            aria-checked={tool === 'brush'}
            role="radio"
            className={`rounded-[var(--radius-pill)] bg-white px-3 py-1.5 shadow-sm ${
              tool === 'brush' ? 'ring-2 ring-[var(--color-joseph-primary)]' : 'opacity-60'
            }`}
          >
            🖌️ 筆刷
          </button>
        </div>
        {tool === 'brush' && (
          <label className="flex items-center gap-2 text-xs text-[var(--color-muted)]">
            radius
            <input
              type="range"
              min={1}
              max={120}
              step={1}
              value={brushRadius}
              onChange={(e) => setBrushRadius(Number(e.target.value))}
              className="w-32"
            />
            <span className="tabular-nums w-10 text-[var(--color-fg)]">{brushRadius}px</span>
          </label>
        )}
      </div>

      <Palette palette={palette} activeIdx={colorIdx} onPick={setColorIdx} />

      <div className="flex justify-between text-sm">
        <button
          onClick={onUndo}
          className="rounded-[var(--radius-pill)] border border-[var(--color-muted)]/30 px-4 py-2 disabled:opacity-40"
        >
          ↶ 上一步
        </button>
        <button
          onClick={onReset}
          className="text-[var(--color-muted)] underline underline-offset-4"
        >
          重畫
        </button>
      </div>

      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
            className="pointer-events-none fixed top-6 left-1/2 -translate-x-1/2 rounded-[var(--radius-pill)] bg-[var(--color-fg)] px-4 py-2 text-sm text-white shadow"
            role="status"
          >
            {toast}
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  )
}

function Palette({
  palette,
  activeIdx,
  onPick,
}: {
  palette: readonly Swatch[]
  activeIdx: number
  onPick: (i: number) => void
}) {
  return (
    <div className="flex flex-wrap justify-center gap-2" role="radiogroup" aria-label="palette">
      {palette.map((sw, i) => (
        <motion.button
          key={sw.hex}
          onClick={() => onPick(i)}
          whileTap={{ scale: 0.92 }}
          animate={i === activeIdx ? { scale: 1.05 } : { scale: 1 }}
          transition={{ type: 'spring', stiffness: 400, damping: 25 }}
          className={`relative size-12 rounded-full ${
            i === activeIdx
              ? 'ring-2 ring-[var(--color-fg)] ring-offset-2 ring-offset-[var(--color-bg)]'
              : 'ring-1 ring-black/10'
          }`}
          style={{ backgroundColor: sw.hex }}
          aria-label={sw.nameZh}
          aria-checked={i === activeIdx}
          role="radio"
        />
      ))}
    </div>
  )
}

// ---------- programmatic placeholder lineart (fallback) ----------

function drawPlaceholderLineart(canvas: HTMLCanvasElement) {
  const ctx = canvas.getContext('2d')!
  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, canvas.width, canvas.height)
  ctx.strokeStyle = '#1a1a1a'
  ctx.lineWidth = 2
  ctx.lineJoin = 'round'
  ctx.lineCap = 'round'
  roundedRect(ctx, 30, 30, canvas.width - 60, canvas.height - 60, 24)
  ctx.stroke()
  ctx.beginPath()
  ctx.arc(190, 200, 100, 0, Math.PI * 2)
  ctx.stroke()
  ctx.beginPath()
  ctx.moveTo(60, 470)
  ctx.bezierCurveTo(180, 320, 300, 520, 540, 410)
  ctx.stroke()
  ctx.beginPath()
  ctx.rect(420, 320, 30, 110)
  ctx.stroke()
  ctx.beginPath()
  ctx.arc(435, 290, 60, 0, Math.PI * 2)
  ctx.stroke()
  ctx.beginPath()
  ctx.rect(60, 370, 130, 100)
  ctx.stroke()
  ctx.beginPath()
  ctx.moveTo(60, 370)
  ctx.lineTo(125, 310)
  ctx.lineTo(190, 370)
  ctx.closePath()
  ctx.stroke()
  ctx.beginPath()
  ctx.rect(105, 415, 40, 55)
  ctx.stroke()
}

function roundedRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
) {
  ctx.beginPath()
  ctx.moveTo(x + r, y)
  ctx.arcTo(x + w, y, x + w, y + h, r)
  ctx.arcTo(x + w, y + h, x, y + h, r)
  ctx.arcTo(x, y + h, x, y, r)
  ctx.arcTo(x, y, x + w, y, r)
  ctx.closePath()
}

function binarizeCanvas(canvas: HTMLCanvasElement) {
  const ctx = canvas.getContext('2d')!
  const id = ctx.getImageData(0, 0, canvas.width, canvas.height)
  const data = id.data
  for (let i = 0; i < data.length; i += 4) {
    const lum = 0.299 * data[i]! + 0.587 * data[i + 1]! + 0.114 * data[i + 2]!
    const bw = lum < 128 ? 0 : 255
    data[i] = bw
    data[i + 1] = bw
    data[i + 2] = bw
    data[i + 3] = 255
  }
  ctx.putImageData(id, 0, 0)
}

// ---------- animation helpers ----------

function animateRippleReveal(
  canvas: HTMLCanvasElement,
  tempCanvas: HTMLCanvasElement,
  candidate: FillCandidate,
  durationMs: number,
): Promise<void> {
  const ctx = canvas.getContext('2d')!
  const tempCtx = tempCanvas.getContext('2d')!
  tempCtx.putImageData(candidate.after, 0, 0)
  const radius = Math.max(0, (candidate.result.maxDistance ?? 0) + 3)

  return new Promise((resolve, reject) => {
    let start = -1
    function frame(now: number) {
      if (start < 0) start = now
      const t = Math.max(0, Math.min(1, (now - start) / durationMs))
      const eased = 1 - Math.pow(1 - t, 2)
      const r = Math.max(0, radius * eased)
      try {
        ctx.putImageData(candidate.before, 0, 0)
        ctx.save()
        ctx.beginPath()
        ctx.arc(candidate.startX, candidate.startY, r, 0, Math.PI * 2)
        ctx.clip()
        ctx.drawImage(tempCanvas, 0, 0)
        ctx.restore()
      } catch (err) {
        reject(err)
        return
      }
      if (t < 1) requestAnimationFrame(frame)
      else resolve()
    }
    requestAnimationFrame(frame)
  })
}

function fadeBetween(
  canvas: HTMLCanvasElement,
  tempCanvas: HTMLCanvasElement,
  before: ImageData,
  after: ImageData,
  durationMs: number,
): Promise<void> {
  const ctx = canvas.getContext('2d')!
  const tempCtx = tempCanvas.getContext('2d')!
  tempCtx.putImageData(after, 0, 0)
  return new Promise((resolve) => {
    let start = -1
    function frame(now: number) {
      if (start < 0) start = now
      const t = Math.max(0, Math.min(1, (now - start) / durationMs))
      ctx.putImageData(before, 0, 0)
      ctx.save()
      ctx.globalAlpha = t
      ctx.drawImage(tempCanvas, 0, 0)
      ctx.restore()
      if (t < 1) requestAnimationFrame(frame)
      else {
        ctx.globalAlpha = 1
        ctx.putImageData(after, 0, 0)
        resolve()
      }
    }
    requestAnimationFrame(frame)
  })
}
